import { Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { AxiosError, AxiosResponse } from 'axios';

import { useDeviceStore } from '@/stores/device';

import { api } from './_http';

// 닉네임 중복 확인(가입시)
export type banner_bottom = {
	banner_image_url?: string;
	banner_url?: string;
	banner_mo_url?: string;
	hot_ranking_url?: string;
	hot_ranking_banner_image_mo_path?: string;
	hot_ranking_banner_image_pc_path?: string;
};
export type banner_top = {
	banner_image_url: string;
	banner_url: string;
	banner_mo_url: string;
};
export type banner_middle_top = {
	banner_image_url: string;
	banner_url: string;
	banner_mo_url: string;
};
export type banner_middle_bottom = {
	banner_image_url: string;
	banner_url: string;
	banner_mo_url: string;
};
export type master_best_rise_one = {
	event_name: string;
	file_path: string;
	master_best_rise_ratio: string;
	master_name: string;
	master_pk: number;
};

type master_best_rise_six = {
	event_name: string;
	file_path: string;
	master_best_rise_ratio: string;
	master_name: string;
	master_pk: number;
};

type master_best_rise_thr = {
	event_name: string;
	file_path: string;
	master_best_rise_ratio: string;
	master_name: string;
	master_pk: number;
};
export type masterpick_ranking_five = {
	allocation_score: number;
	file_path: string;
	growth_score: number;
	master_name: string;
	master_pk: number;
	profitability_score: number;
	stability_score: number;
	total_score: number;
	value_score: number;
	event_name?: string;
	master_investment_pk?: number;
};

type ranking_mediumlarge = {
	file_path: string;
	file_pk: number;
	master_name: string;
	master_pk: number;
	master_rate_competition_division: string;
	master_rate_competition_rank: string;
	master_rate_competition_ratio: string;
	master_rate_competition_stock: string;
};

export type youtube = {
	youtube_thumbnail_url: string;
	youtube_url: string;
};
type GetHomeResType = {
	data: {
		banner_bottom: banner_bottom[];
		banner_top: banner_top[];
		banner_middle_top: banner_middle_top[];
		banner_middle_bottom: banner_middle_bottom[];
		master_best_rise_one: master_best_rise_one[];
		master_best_rise_six: master_best_rise_six[];
		master_best_rise_thr: master_best_rise_thr[];
		masterpick_ranking_five: masterpick_ranking_five[];
		ranking_mediumlarge: ranking_mediumlarge[];
		ranking_mediumsmall: ranking_mediumlarge[];
		ranking_small: ranking_mediumlarge[];
		youtube: youtube[];
	};
	success: boolean;
};

export const useGetHomeApi = () => {
	const deviceStore = useDeviceStore();
	const url = '/v1/front/getHome';
	return useQuery<AxiosResponse, AxiosError, GetHomeResType>({
		queryKey: [url],
		queryFn: () => api.get(url, { device: deviceStore.getDevice }),
		select: (res) => res.data,
	});
};

type GetMasterBestFiveResType = {
	data: {
		masterpick_ranking_five: masterpick_ranking_five[];
	};
	success: boolean;
};
export const useGetMasterBestFiveApi = () => {
	const deviceStore = useDeviceStore();
	const url = '/v1/front/getMasterBestFive';
	return useQuery<AxiosResponse, AxiosError, GetMasterBestFiveResType>({
		queryKey: [url],
		queryFn: () => api.get(url, { device: deviceStore.getDevice }),
		select: (res) => res.data,
	});
};

// 팝업 조회
type GetPopupParams = {
	exposure_location: string;
};
export type GetPopupResType = {
	popup_pk: number;
	popup_image: string;
	popup_url: string;
	popup_mo_url: string;
	popup_name: string;
};
export const useGetPopupApi = ({ enabled, params }: { enabled: Ref<boolean>; params: GetPopupParams }) => {
	const url = '/v1/front/getPopup';
	return useQuery<AxiosResponse, AxiosError, GetPopupResType[]>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params),
		select: (res) => res.data?.data?.popup ?? [],
		staleTime: 1000 * 60 * 5,
		enabled: enabled,
	});
};
//----------------------------------------------------------------------------------------------------------
//홈 조회(미국주식)
type GetHomeData = {
	weekly_rise: WeeklyRise[];
	best_rise_one: BestRiseOne[];
	best_rise_thr: BestRiseOne[];
	best_rise_six: BestRiseOne[];
	hot_ranking_category: HotRankingCategory[];
};
export type WeeklyRise = {
	weekly_rise_pk: number;
	master_file_path: string;
	master_name: string;
	event_file_path: string;
	event_name: string;
	weekly_rise_ratio: string;
};
export type BestRiseOne = {
	best_rise_file_path: string;
	best_rise_pk: number;
	best_rise_ratio: string;
	event_file_path: string;
	event_name: string;
	master_file_path: string;
	master_name: string;
	event_pk: number;
	master_pk: number;
};

export type HotRankingCategory = {
	hot_ranking_name: string;
	hot_ranking_category_pk: number;
	hot_ranking_image_path: string;
	hot_ranking_pk: number;
};
// 주간 상승률 조회
export const useGetHomeApi_USA = () => {
	const url = '/v1/front/usa/getHome';
	return useQuery<AxiosResponse, AxiosError, GetHomeData>({
		queryKey: [url],
		queryFn: () => api.get(url),
		select: (res) => {
			return res.data.data;
		},
	});
};

//----------------------------------------------------------------------------------------------------------
//홈 조회(일본주식)
// 주간 상승률 조회
export const useGetHomeApi_JPN = () => {
	const url = '/v1/front/jpn/getHome';
	return useQuery<AxiosResponse, AxiosError, GetHomeData>({
		queryKey: [url],
		queryFn: () => api.get(url),
		select: (res) => {
			return res.data.data;
		},
	});
};

// 유튜브 라이브 공개 여부 조회
type LiveBroadcastOnAirRes = {
	livebroadcast: number;
};
export const useGetLiveBroadcastOnAirApi = () => {
	const url = '/v1/front/getLiveBroadcastOnAir';
	return useQuery<AxiosResponse, AxiosError, LiveBroadcastOnAirRes>({
		queryKey: [url],
		queryFn: () => api.get(url),
		select: (res) => {
			return res.data?.data;
		},
	});
};

// 라이브 방송 정보 조회
type LiveBroadcast = {
	livebroadcast_pk: number;
	livebroadcast_enrollment_date: string;
	livebroadcast_modifying_date: string;
	livebroadcast_open_yn: 'Y' | 'N';
	livebroadcast_url: string;
	livebroadcast_target: string;
};
type LiveBroadcastDetailSuccess = {
	success: true;
	data: {
		livebroadcast: LiveBroadcast;
	};
	error?: never;
};
type LiveBroadcastDetailError = {
	success: false;
	data: {
		livebroadcast: LiveBroadcast;
	};
	error: {
		code: string;
	};
};
type LiveBroadcastDetail = LiveBroadcastDetailSuccess | LiveBroadcastDetailError;
export const useGetLiveBroadcastDetailApi = () => {
	const url = '/v1/front/getLiveBroadcastDetail';
	return useQuery<AxiosResponse, AxiosError, LiveBroadcastDetail>({
		queryKey: [url],
		queryFn: () => api.get(url),
		select: (res) => {
			return res.data;
		},
		enabled: false,
	});
};
